import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  Backdrop
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Send,
  MoreVert,
  AttachFile,
  EmojiEmotions,
  Message as MessageIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { syncTelegramMessages } from '../services/telegramService';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';

const CHATS_PAGE_SIZE = 20;
const MESSAGES_PAGE_SIZE = 50;

// Stile personalizzato per la scrollbar (minimalista e moderna)
const scrollbarStyles = {
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255, 255, 255, 0.1) transparent',
};

function TelegramMessages() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [messageText, setMessageText] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Stati per la paginazione
  const [chatsLimit, setChatsLimit] = useState(CHATS_PAGE_SIZE);
  const [messagesLimit, setMessagesLimit] = useState(MESSAGES_PAGE_SIZE);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  // Recupera e raggruppa le chat da Dexie con limite
  const chatList = useLiveQuery(async () => {
    const uniqueChatsMap = new Map();
    let count = 0;
    let totalScanned = 0;

    // Usiamo un cursore per efficienza
    await db.telegramMessages
      .orderBy('date')
      .reverse()
      .until(() => uniqueChatsMap.size >= chatsLimit)
      .each(msg => {
        totalScanned++;
        if (!uniqueChatsMap.has(msg.chat)) {
          uniqueChatsMap.set(msg.chat, {
            id: msg.chat,
            lastMessage: msg.text || '',
            time: new Date(msg.date).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
            date: msg.date
          });
        }
      });

    const result = [];
    for (const [chatId, chatData] of uniqueChatsMap) {
      const userInfo = await db.users.where('user').equals(chatId.toString()).first();
      let displayName = `Chat ${chatId}`;
      if (userInfo) {
        displayName = userInfo.username || `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim() || displayName;
      }
      result.push({ ...chatData, name: displayName });
    }

    return result;
  }, [chatsLimit]);

  useEffect(() => {
    handleSync();
  }, []);

  const handleSync = async () => {
    setLoading(true);
    try {
      await syncTelegramMessages();
    } catch (error) {
      console.error("Errore durante la sincronizzazione:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchChatMessages = async () => {
      if (selectedChat) {
        // Recuperiamo i messaggi per la chat selezionata
        // Usiamo reverse() e limit() per prendere gli ultimi 'messagesLimit' messaggi, 
        // ma poi li ordiniamo cronologicamente (dal più vecchio al più nuovo)
        const msgs = await db.telegramMessages
          .where('chat')
          .equals(selectedChat.id)
          .reverse()
          .limit(messagesLimit)
          .toArray();
        
        // Ordina per 'message' ID (dal più piccolo sopra al più grande sotto)
        const sortedMsgs = msgs.sort((a, b) => (a.message || 0) - (b.message || 0));
        
        setMessages(sortedMsgs.map(m => ({
          id: m.telegram_message_id || m.message || Math.random(),
          text: m.text || '',
          sender: m.user === m.bot ? 'me' : 'them',
          time: new Date(m.date).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
          rawDate: m.date
        })));

        const count = await db.telegramMessages.where('chat').equals(selectedChat.id).count();
        setHasMoreMessages(count > messagesLimit);
      }
    };
    fetchChatMessages();
  }, [selectedChat, messagesLimit]);

  const filteredChats = useMemo(() => {
    if (!chatList) return [];
    const filtered = chatList.filter(chat => 
      chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chat.id.toString().includes(searchQuery)
    );
    return filtered;
  }, [chatList, searchQuery]);

  const handleSendMessage = () => {
    if (!messageText.trim()) return;
    
    const newMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'me',
      time: new Date().toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    };
    
    setMessages([...messages, newMessage]);
    setMessageText('');
  };

  const handleLoadMoreChats = () => {
    setChatsLimit(prev => prev + CHATS_PAGE_SIZE);
  };

  const handleLoadMoreMessages = () => {
    setMessagesLimit(prev => prev + MESSAGES_PAGE_SIZE);
  };

  if (!chatList && loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#121212' }}>
        <Loader />
        <Typography sx={{ mt: 2, color: 'white' }}>Caricamento chat...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100vh', 
      width: '100vw', 
      display: 'flex', 
      overflow: 'hidden',
      bgcolor: '#121212', 
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 1300 
    }}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}
        open={loading && chatList?.length > 0}
      >
        <Loader />
        <Typography variant="h6">Sincronizzazione in corso...</Typography>
      </Backdrop>

      {!isMobile ? (
        <>
          <Box sx={{ 
            width: '350px', 
            borderRight: '1px solid rgba(255,255,255,0.1)',
            flexShrink: 0
          }}>
            <ChatList 
              isMobile={isMobile} 
              navigate={navigate} 
              searchQuery={searchQuery} 
              setSearchQuery={setSearchQuery} 
              filteredChats={filteredChats} 
              setSelectedChat={(chat) => {
                setSelectedChat(chat);
                setMessagesLimit(MESSAGES_PAGE_SIZE);
              }} 
              selectedChat={selectedChat}
              theme={theme}
              onSync={handleSync}
              onLoadMore={handleLoadMoreChats}
              hasMore={chatList?.length >= chatsLimit}
            />
          </Box>
          
          <Box sx={{ flexGrow: 1 }}>
            {selectedChat ? (
              <ChatWindow 
                isMobile={isMobile} 
                setSelectedChat={setSelectedChat} 
                selectedChat={selectedChat} 
                messages={messages} 
                messageText={messageText} 
                setMessageText={setMessageText} 
                handleSendMessage={handleSendMessage}
                theme={theme}
                onLoadMoreMessages={handleLoadMoreMessages}
                hasMoreMessages={hasMoreMessages}
              />
            ) : (
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center',
                color: 'rgba(255,255,255,0.3)'
              }}>
                <MessageIcon sx={{ fontSize: 100, mb: 2, opacity: 0.1 }} />
                <Typography variant="h6">Seleziona una chat per iniziare a messaggiare</Typography>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ width: '100%', height: '100%' }}>
          {selectedChat ? (
            <ChatWindow 
              isMobile={isMobile} 
              setSelectedChat={setSelectedChat} 
              selectedChat={selectedChat} 
              messages={messages} 
              messageText={messageText} 
              setMessageText={setMessageText} 
              handleSendMessage={handleSendMessage}
              theme={theme}
              onLoadMoreMessages={handleLoadMoreMessages}
              hasMoreMessages={hasMoreMessages}
            />
          ) : (
            <ChatList 
              isMobile={isMobile} 
              navigate={navigate} 
              searchQuery={searchQuery} 
              setSearchQuery={setSearchQuery} 
              filteredChats={filteredChats} 
              setSelectedChat={(chat) => {
                setSelectedChat(chat);
                setMessagesLimit(MESSAGES_PAGE_SIZE);
              }} 
              selectedChat={selectedChat}
              theme={theme}
              onSync={handleSync}
              onLoadMore={handleLoadMoreChats}
              hasMore={chatList?.length >= chatsLimit}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

const ChatList = ({ isMobile, navigate, searchQuery, setSearchQuery, filteredChats, setSelectedChat, selectedChat, theme, onSync, onLoadMore, hasMore }) => (
  <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {!isMobile && (
          <IconButton onClick={() => navigate('/telegram')} sx={{ color: 'white' }}>
            <ArrowBack />
          </IconButton>
        )}
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
          Chat
        </Typography>
      </Box>
      <IconButton onClick={onSync} sx={{ color: 'white' }} title="Sincronizza messaggi">
        <RefreshIcon />
      </IconButton>
    </Box>

    <Box sx={{ px: 2, pb: 2 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Cerca chat..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: 'rgba(255,255,255,0.5)' }} />
            </InputAdornment>
          ),
          sx: { 
            borderRadius: '20px', 
            bgcolor: 'rgba(255,255,255,0.05)',
            '& fieldset': { border: 'none' },
            color: 'white'
          }
        }}
      />
    </Box>

    <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

    <List sx={{ 
      flexGrow: 1, 
      overflowY: 'auto', 
      py: 0,
      ...scrollbarStyles // Applica gli stili della scrollbar
    }}>
      {filteredChats.map((chat) => (
        <ListItem 
          key={chat.id} 
          button 
          onClick={() => setSelectedChat(chat)}
          selected={selectedChat?.id === chat.id}
          sx={{
            py: 1.5,
            '&.Mui-selected': {
              bgcolor: 'rgba(77, 171, 86, 0.2)',
              '&:hover': { bgcolor: 'rgba(77, 171, 86, 0.3)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' }
          }}
        >
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              {chat.name.charAt(0)}
            </Avatar>
          </ListItemAvatar>
          <ListItemText 
            primary={
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                  {chat.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {chat.time}
                </Typography>
              </Box>
            }
            secondary={
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255,255,255,0.6)', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  maxWidth: '220px'
                }}
              >
                {chat.lastMessage}
              </Typography>
            }
          />
        </ListItem>
      ))}
      {hasMore && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Button 
            size="small" 
            onClick={onLoadMore} 
            startIcon={<ExpandMoreIcon />}
            sx={{ color: 'rgba(255,255,255,0.5)' }}
          >
            Carica altre chat
          </Button>
        </Box>
      )}
    </List>
  </Box>
);

const ChatWindow = ({ isMobile, setSelectedChat, selectedChat, messages, messageText, setMessageText, handleSendMessage, theme, onLoadMoreMessages, hasMoreMessages }) => {
  const scrollRef = useRef(null);

  // Auto-scroll verso il fondo quando cambiano i messaggi
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'rgba(0,0,0,0.2)' }}>
      <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Toolbar>
          {isMobile && (
            <IconButton onClick={() => setSelectedChat(null)} sx={{ color: 'white', mr: 1 }}>
              <ArrowBack />
            </IconButton>
          )}
          <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
            {selectedChat.name.charAt(0)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white', lineHeight: 1.2 }}>
              {selectedChat.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              ID: {selectedChat.id}
            </Typography>
          </Box>
          <IconButton sx={{ color: 'white' }}>
            <Search />
          </IconButton>
          <IconButton sx={{ color: 'white' }}>
            <MoreVert />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box 
        ref={scrollRef}
        sx={{ 
          flexGrow: 1, 
          overflowY: 'auto', 
          p: 2, 
          display: 'flex', 
          flexDirection: 'column',
          gap: 1,
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(77, 171, 86, 0.05) 0%, transparent 100%)',
          ...scrollbarStyles // Applica gli stili della scrollbar
        }}
      >
        {hasMoreMessages && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Button 
              size="small" 
              onClick={onLoadMoreMessages}
              sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none' }}
            >
              Carica messaggi precedenti
            </Button>
          </Box>
        )}
        {messages.map((msg) => (
          <Box 
            key={msg.id} 
            sx={{ 
              alignSelf: msg.sender === 'me' ? 'flex-end' : 'flex-start',
              maxWidth: '80%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Paper 
              elevation={1}
              sx={{ 
                p: 1.5, 
                borderRadius: msg.sender === 'me' ? '15px 15px 0 15px' : '15px 15px 15px 0',
                bgcolor: msg.sender === 'me' ? theme.palette.primary.main : 'rgba(255,255,255,0.1)',
                color: 'white'
              }}
            >
              <Typography variant="body2">{msg.text}</Typography>
              <Typography variant="caption" sx={{ 
                display: 'block', 
                textAlign: 'right', 
                mt: 0.5, 
                opacity: 0.7,
                fontSize: '0.65rem'
              }}>
                {msg.time}
              </Typography>
            </Paper>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton sx={{ color: 'rgba(255,255,255,0.6)' }}>
            <AttachFile />
          </IconButton>
          <TextField
            fullWidth
            size="small"
            placeholder="Scrivi un messaggio..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSendMessage();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    <EmojiEmotions />
                  </IconButton>
                </InputAdornment>
              ),
              sx: { 
                borderRadius: '20px', 
                bgcolor: 'rgba(255,255,255,0.05)',
                '& fieldset': { border: 'none' },
                color: 'white'
              }
            }}
          />
          <IconButton 
            onClick={handleSendMessage}
            disabled={!messageText.trim()}
            sx={{ 
              bgcolor: theme.palette.primary.main, 
              color: 'white',
              '&:hover': { bgcolor: theme.palette.primary.dark },
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' }
            }}
          >
            <Send />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default TelegramMessages;
